/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from "@angular/common";
import {Component, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import {FabMessageService} from "@fabric/components/message";
import {
  GridApi,
  GridReadyEvent, PostSortRowsParams,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {
  DISMISS_MSG,
  FCORG_BLACKLIST,
  VEHICLE_PROPULSION_COLDEF,
  VEHICLE_PROPULSION_COLDEF_NO_FILTER
} from "../../../common/constants";
import {getDefaultYearRange, sortVehicleScenarios} from "../../../core/helpers/generic-helpers";
import {StateService} from "../../../core/state/state.service";
import {GlobalScenario} from "../../../models/global-scenario-dto.model";
import {VehicleScenario} from "../../../models/vehicle-scenario-dto.model";
import {ExcelExportService} from "../../../services/excel-export.service";
import {PropulsionScenarioService} from "../../../services/propulsion-scenario-service";
import {VehicleScenarioService} from "../../../services/vehicle-scenario.service";

@Component({
    selector: 'app-propulsion-consolidation-report',
    templateUrl: './propulsion-consolidation-report.component.html',
    styleUrl: './propulsion-consolidation-report.component.scss',
    standalone: false
})
export class PropulsionConsolidationReportComponent implements OnInit {
  protected readonly VEHICLE_PROPULSION_COLDEF_NO_FILTER = VEHICLE_PROPULSION_COLDEF_NO_FILTER;
  protected readonly VEHICLE_PROPULSION_COLDEF = VEHICLE_PROPULSION_COLDEF;

  gridApi: GridApi;

  reportType: string = 'VolYrAcross';

  showSpinner: boolean;
  includeDateRange: boolean;
  includeNoPen: boolean;

  availableScenarios: any[] = [];

  selectedVehicleScenarios: any[] = [];
  selectedGlobalScenario: GlobalScenario;
  memberScenarios: VehicleScenario[] = [];
  memberScenariosToRemove: VehicleScenario[] = [];
  selectedSourceType: string = 'fcorg';

  yearRange: number[];
  startYear: number;
  endYear: number;

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false
  };

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<VehicleScenario>,
  ) => {
    // Only post sort rows if the user has a primary FCORG preference
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      // here we put the Primary FCORG rows on top while preserving the sort order if the user has a primary FCORG preference
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.fcorg : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };

  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              readonly vehicleScenarioService: VehicleScenarioService,
              readonly propulsionScenarioService: PropulsionScenarioService,
              readonly stateService: StateService,
              readonly router: Router) {
  }

  ngOnInit() {
    this.getYearRange();
    this.onSourceChange();
  }

  /**
   * Gets the grid API and data when the grid is ready
   * @param params the parameters generated by the grid event
   */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }


  /**
   * Get list of Vehicle Scenarios to populate table for user selection
   */
  getVehicleScenarios(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.getVehicleScenariosForSelect(this.stateService.getUserGmid(), false).subscribe({
      next: (data) => {
        // Filter out all obsolete FCORGs
        this.availableScenarios = data.filter(row => !FCORG_BLACKLIST.includes(row.fcorg));
        // Filter the list of available scenarios to only show vehicle scenarios with propulsion scenarios attached to it
        this.availableScenarios = this.availableScenarios.filter(row => row.propulsionScenario.id !== null);
        // Sort the results by FCORG and ID
        this.availableScenarios = sortVehicleScenarios(this.availableScenarios);
      },
      error: (err) => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;

        // If they are on the By TVP Scenarios tab, filter only to show TVP scenarios,
        // otherwise show the list with the TVP scenarios filtered out
        if (this.selectedSourceType === 'tvp') {
          this.availableScenarios = this.availableScenarios.filter(row => row.fcorg.includes('TVP'));
        } else {
          this.availableScenarios = this.availableScenarios.filter(row => !row.fcorg.includes('TVP'));
        }
      }
    });
  }


  /**
   * Gets the year range for the reports
   */
  getYearRange(): void {
    const {yearRange, startYear, endYear} = getDefaultYearRange();
    this.yearRange = yearRange;
    this.startYear = startYear;
    this.endYear = endYear;
  }

  /**
   * Whenever use changes between FCORG and Global scenarios, reset variables and fetch the proper scenarios
   */
  onSourceChange(): void {
    // Reset variables
    this.selectedVehicleScenarios = [];
    this.selectedGlobalScenario = undefined;
    this.memberScenarios = [];
    this.memberScenariosToRemove = [];
    this.includeNoPen = false;
    this.includeDateRange = false;
    // Fetch the proper scenarios
    if (this.selectedSourceType !== 'global') {
      this.getVehicleScenarios();
    }
  }

  /**
   * Sets the scenario as selected
   * @param event the clicked scenario row
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedVehicleScenarios.push(event.data);
    } else {
      this.selectedVehicleScenarios = this.selectedVehicleScenarios.filter(element => element != event.data);
    }
  }

  /**
   * Calls the proper function to fetch the member scenarios based on the selected scenario
   * @param event The event generated by the child
   */
  onSelectGlobal(event: any) {
    this.memberScenarios = event.memberScenarios;
    this.selectedGlobalScenario = event.globalScenario;
  }

  /**
   * Sets the member scenario as selected
   * @param event the clicked member scenario row event
   */
  onMemberSelect(event): void {
    if (event.node.selected) {
      this.memberScenariosToRemove.push(event.data);
    } else {
      this.memberScenariosToRemove = this.memberScenariosToRemove.filter(element => element != event.data);
    }
  }

  /**
   * When a user adds an available vehicle scenario to the selected member scenarios
   */
  onAddMemberScenario(): void {
    // If the selectedVehicleScenarios has multiple of the same FCORGs, throw an error
    const fcorgList = this.memberScenarios.map(col => col.fcorg).concat(this.selectedVehicleScenarios.map(col => col.fcorg));
    if (fcorgList.filter((v, i) => fcorgList.indexOf(v) !== i).length > 0) {
      this.snackBar.displayError('Cannot have more than 1 of the same FCORG in the member scenario table', DISMISS_MSG);
    } else {
      this.memberScenarios = sortVehicleScenarios(this.memberScenarios.concat(this.selectedVehicleScenarios));
      this.availableScenarios = this.availableScenarios.filter(row => !this.selectedVehicleScenarios.includes(row));
    }

    // Clear out the selected vehicle scenarios
    this.selectedVehicleScenarios = [];
    this.gridApi.deselectAll();

  }

  /**
   * When the user removes an already selected member scenario back to the available scenarios
   */
  onRemoveMemberScenario(): void {
    if (this.memberScenariosToRemove.length > 0) {
      // Update the data used in the tables
      this.availableScenarios = sortVehicleScenarios(this.availableScenarios.concat(this.memberScenariosToRemove));
      this.memberScenarios = this.memberScenarios
      .filter((scenario) => !this.memberScenariosToRemove.includes(scenario));

      // Clear out the selected member scenarios to remove
      this.memberScenariosToRemove = [];
      this.selectedVehicleScenarios = [];
    }
  }

  /**
   * Export the report
   */
  onRunReport(): void {
    this.showSpinner = true;
    const vehicleScenarioIds = this.memberScenarios.map(row => row.vehScenId).join(', ');
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const idList = this.memberScenarios.map(scenario => scenario.propulsionScenario.id);
    let scenarioString: string;
    if (this.selectedSourceType == 'global') {
      scenarioString = this.selectedGlobalScenario.id.toString();
    } else {
      scenarioString = idList.length > 1 ? idList.sort()[0] + "-" + idList.sort()[idList.length - 1] : idList[0]?.toString();
    }
    const fileName = 'PropulsionConsolidation_' + scenarioString + "_" + currDate;
    this.propulsionScenarioService.getConsolidationReport(vehicleScenarioIds,
      this.startYear, this.endYear, this.stateService.getUserGmid(), this.reportType,
      this.includeDateRange ? 1 : 0, this.includeNoPen ? 1 : 0).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Successfully retrieved data. Exporting...', DISMISS_MSG);
      });
  }

  /**
   * Reset the checkbox values on radio button changes
   */
  onRadioChange(): void {
    this.includeDateRange = false;
    this.includeNoPen = false;
  }

}
